import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { pushService } from '@/services/push.service';

const DISMISS_KEY = 'attendance_push_optin_dismissed';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function dismissedRecently(): boolean {
    try {
        const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return Date.now() - ts < DISMISS_TTL_MS;
    } catch { return false; }
}

function urlBase64ToUint8(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

function arrayBufferToBase64Url(buf: ArrayBuffer | null): string {
    if (!buf) return '';
    const u8 = new Uint8Array(buf);
    let s = '';
    for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
    return btoa(s).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

export function PushOptInBanner() {
    const { t } = useI18n();
    const { isAuthenticated } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (typeof window === 'undefined') return;
        if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
        if (Notification.permission !== 'default') return;
        if (dismissedRecently()) return;
        // Tampilkan setelah delay supaya tidak muncul di first paint
        const t = setTimeout(() => setShow(true), 5_000);
        return () => clearTimeout(t);
    }, [isAuthenticated]);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        setShow(false);
    };

    const enable = async () => {
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') {
                dismiss();
                return;
            }
            // Ambil VAPID public key dari backend
            const keyRes = await pushService.getPublicKey();
            const publicKey = (keyRes.data as any).publicKey;
            if (!publicKey) throw new Error('VAPID belum dikonfigurasi server');

            const reg = await navigator.serviceWorker.ready;
            const subscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8(publicKey).buffer as ArrayBuffer,
            });

            const json: any = subscription.toJSON();
            await pushService.subscribe({
                endpoint: json.endpoint,
                keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
                workspaceId: currentWorkspace?._id,
            });

            showToast({ variant: 'success', description: t('push.toast_enabled') });
            setShow(false);
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.message || t('push.toast_failed') });
        } finally {
            setBusy(false);
        }
    };

    if (!show) return null;

    return (
        <div className="mx-4 mt-2 mb-3 rounded-2xl p-4 shadow-md max-w-md sm:mx-auto"
             style={{ background: 'linear-gradient(135deg, #fefce8, #fef3c7)', border: '1px solid #fde68a' }}>
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-200 flex items-center justify-center shrink-0">
                    <Bell className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-amber-900">{t('push.optin_title')}</p>
                    <p className="text-xs text-amber-800 mt-0.5">{t('push.optin_body')}</p>
                    <div className="flex gap-2 mt-3">
                        <Button onClick={enable} disabled={busy} size="sm" className="bg-amber-600 hover:bg-amber-700">
                            {busy ? t('common.processing') : t('push.optin_btn')}
                        </Button>
                        <Button onClick={dismiss} variant="ghost" size="sm" className="text-amber-700">{t('push.optin_dismiss')}</Button>
                    </div>
                </div>
                <button onClick={dismiss} className="text-amber-600 hover:text-amber-800 p-1 -mr-1">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
